<script setup lang="ts">
  import Table from '@/components/table/Table.vue'
  import TableColumnSheet from '@/components/table/TableColumnSheet.vue'
  import TopBar from '@/components/TopBar.vue'
  import { useProjectStore } from '@/store/project.store'
  import type { Project } from '@/types/nest/projects'
  import NewProjectSheet from '@/views/projects/components/NewProjectSheet.vue'
  import {
    ConfirmationDialog,
    UiButton,
    useToastStore,
    type Column,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'

  const projectStore = useProjectStore()
  const toastStore = useToastStore()
  const router = useRouter()

  const columns: Column[] = [
    { key: 'name', label: 'Naam', searchable: true },
    { key: 'customerId', label: 'Klant ID', searchable: true },
    { key: 'id', label: 'ID', searchable: true },
    { key: 'actions', label: 'Acties', sticky: 'right' },
  ]

  const defaultColumns = ['name', 'actions']

  const visibleColumns = ref<Column[]>([])
  const showColumnSheet = ref<boolean>(false)
  const showNewProjectSheet = ref<boolean>(false)
  const projectToDelete = ref<Project | null>(null)

  const showConfirmationDialog = computed({
    get: () => projectToDelete.value != null,
    set: value => {
      if (!value) projectToDelete.value = null
    },
  })

  onMounted(async () => {
    await projectStore.fetchProjects()
  })

  async function deleteProject() {
    if (!projectToDelete.value?.id) return
    try {
      await projectStore.deleteProject(projectToDelete.value.id)
      projectToDelete.value = null
      toastStore.success('Project succesvol verwijderd')
    } catch (error) {
      toastStore.error('Error deleting project:')
      throw error
    }
  }
</script>

<template>
  <TopBar>
    <UiButton v-if="projectStore.projects.length > 0" icon="more" @click="showColumnSheet = true" />

    <UiButton variant="prominent" icon="plus" @click="showNewProjectSheet = true" />
  </TopBar>

  <TableColumnSheet
    v-model:show="showColumnSheet"
    v-model:visible-columns="visibleColumns"
    :columns="columns"
    :default-columns="defaultColumns"
  />

  <NewProjectSheet v-model:show="showNewProjectSheet" @created="projectStore.fetchProjects" />

  <ConfirmationDialog
    v-model:show="showConfirmationDialog"
    :confirm="deleteProject"
    :title="`Are you sure you want to remove: ${projectToDelete?.name}?`"
    body="This action cannot be undone."
    confirmButtonText="Remove"
    confirmButtonColor="negative"
    class="confirm-delete-project"
  />

  <div class="layout">
    <Table :rows="projectStore.projects" :columns="visibleColumns" row-key="id">
      <template #cell-actions="{ row }">
        <div class="button-group">
          <UiButton
            variant="link"
            icon="edit"
            @click="router.push({ name: 'project-details', params: { uuid: row.id } })"
          />
          <UiButton variant="link" icon="delete" @click="projectToDelete = row" />
        </div>
      </template>
    </Table>
  </div>
</template>

<style lang="css" scoped>
  .modal-overlay.confirm-delete-project {
    z-index: 20;
  }

  .button-group {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-xs);
  }

  .layout {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    gap: var(--spacing-s);
    min-height: calc(100dvh - var(--top-bar-height));
  }
</style>
